<template>
  <!-- Teleport to body so panel overlays everything -->
  <teleport to="body">
    <!-- Backdrop -->
    <transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-black/60 z-[1040]"
        @click="close"
      />
    </transition>

    <!-- Slide-over panel -->
    <transition name="slide-x">
      <aside
        v-if="isOpen"
        class="ai-panel"
      >
        <!-- Header -->
        <header class="relative p-4 border-b border-white/10">
          <h2 class="ai-title font-semibold">Forecast AI Assistant</h2>
          <DxButton
            class="panel-close"
            icon="close"
            styling-mode="text"
            @click="close"
            aria-label="Close assistant"
          />
        </header>

        <!-- Messages container -->
        <div ref="scrollEl" class="scroll-area" @scroll="onScroll">
          <ChatMessage
            v-for="m in messages"
            :key="m.id"
            :role="m.role"
            :content="m.content"
          />
          <!-- Typing indicator -->
          <TypingIndicator v-if="loading" />
        </div>

        <!-- Scroll-to-bottom helper -->
        <button
          v-if="showScrollBtn"
          class="scroll-btn"
          @click="scrollToBottom"
          aria-label="Scroll to latest message"
        >
          ↓
        </button>

        <!-- Composer -->
        <form class="composer-row p-4 border-t border-white/10 flex gap-2 items-end" @submit.prevent="send">
          <textarea
            v-model="draft"
            placeholder="Ask something..."
            rows="1"
            class="composer-input flex-1"
            @keydown.enter.exact.prevent="send"
            @keydown.enter.shift="noop"
          />
          <!-- Send button (orange contained) -->
          <DxButton
            icon="arrowright"
            type="default"
            styling-mode="contained"
            :disabled="!draft.trim()"
            @click="send"
            aria-label="Send message"
          />

        </form>

        <!-- Clear history button – fixed bottom-right of panel -->
        <DxButton
          v-if="isOpen"
          icon="trash"
          type="default"
          styling-mode="contained"
          class="trash-btn"
          :use-submit-behavior="false"
          @click="handleClear"
          aria-label="Clear conversation"
        />
      </aside>
    </transition>
  </teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, watch, nextTick } from 'vue';
import ChatMessage from './ChatMessage.vue';
import TypingIndicator from './TypingIndicator.vue';
import useChat from './useChat.js';

import DxButton from 'devextreme-vue/button';

defineProps({ isOpen: { type: Boolean, default: false } });

const emit = defineEmits(['close']);

// Chat state
const { messages, draft, send, loading, clear } = useChat();

function handleClear() {
  clear();
  nextTick(() => {
    scrollToBottom();
  });
}

function close() { emit('close'); }

// Auto-scroll logic -------------------------------------------------------
const scrollEl = ref(null);
const showScrollBtn = ref(false);

function scrollToBottom() {
  scrollEl.value?.scrollTo({ top: scrollEl.value.scrollHeight, behavior: 'smooth' });
}

function onScroll() {
  if (!scrollEl.value) return;
  const bottomGap = scrollEl.value.scrollHeight - scrollEl.value.scrollTop - scrollEl.value.clientHeight;
  showScrollBtn.value = bottomGap > 120; // show when > 120px away from bottom
}

// watch messages length to auto scroll
watch(
  () => messages.value.length,
  async () => {
    await nextTick();
    scrollToBottom();
  }
);

// dummy
function noop() {}
</script>

<style scoped>

/* -------------------------------------------------------------------------
   Panel sizing & responsive behaviour
   – Desktop: fixed bottom-right, width 32rem (max 90vw)
   – Mobile (<640px): full-width bottom sheet style
------------------------------------------------------------------------- */
.ai-panel {
  position: fixed;
  bottom: 6rem; /* above toggle button */
  right: 1.5rem;
  width: min(32rem, 90vw);
  height: calc(100vh - 8rem); /* gives ~2rem margin top + toggle btn bottom */
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* keep internal scroller responsible for overflow */
  background: #111827; /* gray-900 */
  color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  z-index: 1050;
}

@media (max-width: 639px) {
  .ai-panel {
    left: 0.5rem;
    right: 0.5rem;
    width: auto;
    bottom: 5.5rem; /* a bit tighter on mobile */
    height: calc(100vh - 7rem);
  }
}

.composer-input {
  width: 100%;
  resize: none;
  background: #1f2937; /* gray-800 */
  color: #fff;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  outline: none;
  font-family: inherit;
  min-height: 2.5rem;
  max-height: 10rem;
}
.composer-input::placeholder { color: #9ca3af; }

.composer-input:focus {
  box-shadow: 0 0 0 2px #6366f1; /* indigo-500 */
}

.scroll-btn {
  position: absolute;
  right: 1rem;
  bottom: 8rem;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: #4f46e5;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  cursor: pointer;
  border: none;
}
.scroll-btn:hover { background: #4338ca; }

/* Scrollable area inside panel */
.scroll-area {
  flex: 1 1 auto;
  min-height: 0; /* allow proper overflow within flex column */
  overflow-y: auto;
  /* Make sure the element never grows beyond the available space so
     overflowing content will trigger the native scrollbar instead of
     pushing the whole panel beyond the viewport */
  max-height: 100%;
  -webkit-overflow-scrolling: touch; /* enable momentum scroll on iOS */
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* ------------------------ header tweaks ------------------------------- */
.ai-title {
  font-size: 2rem;
}

.panel-close {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
}


/* composer clear button */

/* Trash button positioning */
.trash-btn {
  position: absolute;
  right: 1rem;
  bottom: 0.0rem;
  min-width: 2.25rem;
  height: 2.25rem;
}

/* icon buttons sizing */
.dx-buttongroup .dx-button, .dx-button {
  min-width: 2.25rem;
  height: 2.25rem;
}

/* Transitions (can also live in global css) */
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from,  .fade-leave-to      { opacity: 0; }

.slide-x-enter-active, .slide-x-leave-active { transition: transform .2s ease; }
.slide-x-enter-from,    .slide-x-leave-to    { transform: translateX(120%); }
</style>
